// src/services/roadAIEngineService.js
// FlowSense MOW+ — Road AI Engine Service
// v2.0 | Enterprise-grade: Circuit Breaker · LRU Cache · Dedup · Retry ·
//        AbortController · Offline Queue · Event Bus · Health Monitor · Typed Errors

import { getAuthToken } from '../utils/auth';
import { config } from '../config';

const API_BASE = config.apiUrl;

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1 — TYPED ERROR HIERARCHY
// Every failure surface produces a distinct, catchable error class.
// ─────────────────────────────────────────────────────────────────────────────

class RoadAIError extends Error {
    constructor(message, code, context = {}) {
        super(message);
        this.name      = 'RoadAIError';
        this.code      = code;
        this.context   = context;
        this.timestamp = new Date().toISOString();
        if (Error.captureStackTrace) Error.captureStackTrace(this, this.constructor);
    }
    toJSON() {
        return { name: this.name, code: this.code, message: this.message, context: this.context, timestamp: this.timestamp };
    }
}

class NetworkError extends RoadAIError {
    constructor(message, context) { super(message, 'NETWORK_ERROR', context); this.name = 'NetworkError'; this.retryable = true; }
}
class AuthError extends RoadAIError {
    constructor(message, context) { super(message, 'AUTH_ERROR', context); this.name = 'AuthError'; this.retryable = false; }
}
class NotFoundError extends RoadAIError {
    constructor(message, context) { super(message, 'NOT_FOUND', context); this.name = 'NotFoundError'; this.retryable = false; }
}
class ValidationError extends RoadAIError {
    constructor(message, context) { super(message, 'VALIDATION_ERROR', context); this.name = 'ValidationError'; this.retryable = false; }
}
class RateLimitError extends RoadAIError {
    constructor(retryAfterMs, context) {
        super(`Rate limited — retry after ${retryAfterMs}ms`, 'RATE_LIMIT', context);
        this.name = 'RateLimitError';
        this.retryable = true;
        this.retryAfterMs = retryAfterMs;
    }
}
class CircuitOpenError extends RoadAIError {
    constructor(endpoint) { super(`Circuit open for ${endpoint} — service unavailable`, 'CIRCUIT_OPEN', { endpoint }); this.name = 'CircuitOpenError'; this.retryable = false; }
}
class TimeoutError extends RoadAIError {
    constructor(endpoint, timeoutMs) { super(`Request to ${endpoint} timed out after ${timeoutMs}ms`, 'TIMEOUT', { endpoint, timeoutMs }); this.name = 'TimeoutError'; this.retryable = true; }
}
class OfflineError extends RoadAIError {
    constructor(endpoint) { super(`Cannot reach ${endpoint} — device is offline`, 'OFFLINE', { endpoint }); this.name = 'OfflineError'; this.retryable = true; }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 2 — STRUCTURED LOGGER
// Lightweight, level-gated logger with structured context. Disabled in prod
// unless explicitly opted in via localStorage.ROAD_AI_DEBUG.
// ─────────────────────────────────────────────────────────────────────────────

const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

class ServiceLogger {
    constructor(namespace) {
        this.namespace = namespace;
        this.level     = this._resolveLevel();
    }
    _resolveLevel() {
        if (process.env.NODE_ENV === 'development') return LEVELS.debug;
        try {
            const override = localStorage.getItem('ROAD_AI_DEBUG');
            if (override) return LEVELS[override] ?? LEVELS.warn;
        } catch { /* localStorage unavailable (SSR, privacy mode) */ }
        return LEVELS.warn;
    }
    _write(level, message, ctx = {}) {
        if (LEVELS[level] < this.level) return;
        const styles = {
            debug: 'color:#6b7280;font-size:11px',
            info:  'color:#3b82f6;font-weight:600',
            warn:  'color:#f59e0b;font-weight:600',
            error: 'color:#ef4444;font-weight:700',
        };
        const prefix = `%c[${this.namespace}][${level.toUpperCase()}]`;
        if (Object.keys(ctx).length > 0) {
            console.log(prefix, styles[level], message, ctx);
        } else {
            console.log(prefix, styles[level], message);
        }
    }
    debug(m, ctx) { this._write('debug', m, ctx); }
    info(m, ctx)  { this._write('info',  m, ctx); }
    warn(m, ctx)  { this._write('warn',  m, ctx); }
    error(m, ctx) { this._write('error', m, ctx); }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 3 — LRU CACHE WITH PER-KEY TTL
// Fixed-capacity LRU using a Map (insertion-order iteration) so the least-
// recently used entry is always Map iteration index 0.
// ─────────────────────────────────────────────────────────────────────────────

class LRUCache {
    constructor(capacity = 64) {
        this.capacity = capacity;
        this._map     = new Map();   // key → { value, expiresAt, hits }
        this._hits    = 0;
        this._misses  = 0;
    }

    get(key) {
        const entry = this._map.get(key);
        if (!entry) { this._misses++; return null; }
        if (Date.now() > entry.expiresAt) {
            this._map.delete(key);
            this._misses++;
            return null;
        }
        // Promote to most-recently-used by re-inserting
        this._map.delete(key);
        entry.hits++;
        this._map.set(key, entry);
        this._hits++;
        return entry.value;
    }

    set(key, value, ttlMs) {
        if (this._map.has(key)) this._map.delete(key); // refresh position
        if (this._map.size >= this.capacity) {
            // Evict LRU (first key in Map)
            this._map.delete(this._map.keys().next().value);
        }
        this._map.set(key, { value, expiresAt: Date.now() + ttlMs, hits: 0 });
    }

    /** Remove all keys matching a prefix — used for targeted invalidation */
    invalidate(prefix) {
        let evicted = 0;
        for (const key of this._map.keys()) {
            if (key.startsWith(prefix)) { this._map.delete(key); evicted++; }
        }
        return evicted;
    }

    /** Remove all expired entries without evicting valid ones */
    prune() {
        const now = Date.now();
        for (const [key, entry] of this._map) {
            if (now > entry.expiresAt) this._map.delete(key);
        }
    }

    clear() { this._map.clear(); }

    get stats() {
        const total = this._hits + this._misses;
        return {
            size:     this._map.size,
            capacity: this.capacity,
            hits:     this._hits,
            misses:   this._misses,
            hitRate:  total > 0 ? ((this._hits / total) * 100).toFixed(1) + '%' : '—',
        };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 4 — CIRCUIT BREAKER
// States: CLOSED (healthy) → OPEN (failing, rejects immediately) →
//         HALF_OPEN (probe allowed) → CLOSED or back to OPEN.
// ─────────────────────────────────────────────────────────────────────────────

const CB_STATE = Object.freeze({ CLOSED: 'CLOSED', OPEN: 'OPEN', HALF_OPEN: 'HALF_OPEN' });

class CircuitBreaker {
    /**
     * @param {object} opts
     * @param {number}  opts.failureThreshold  — consecutive failures before opening (default 5)
     * @param {number}  opts.successThreshold  — successes in HALF_OPEN to close again (default 2)
     * @param {number}  opts.openDurationMs    — how long to stay OPEN before probing (default 30s)
     * @param {function} opts.onStateChange    — callback (newState, oldState)
     */
    constructor({ failureThreshold = 5, successThreshold = 2, openDurationMs = 30_000, onStateChange } = {}) {
        this._state           = CB_STATE.CLOSED;
        this._failures        = 0;
        this._successes       = 0;
        this._lastFailureAt   = null;
        this._openDurationMs  = openDurationMs;
        this._failureThreshold = failureThreshold;
        this._successThreshold = successThreshold;
        this._onStateChange   = onStateChange;
    }

    get state() { return this._state; }

    canRequest() {
        if (this._state === CB_STATE.CLOSED)    return true;
        if (this._state === CB_STATE.HALF_OPEN) return true;
        // OPEN — check if recovery window has elapsed
        if (Date.now() - this._lastFailureAt >= this._openDurationMs) {
            this._transition(CB_STATE.HALF_OPEN);
            return true;
        }
        return false;
    }

    recordSuccess() {
        this._failures = 0;
        if (this._state === CB_STATE.HALF_OPEN) {
            this._successes++;
            if (this._successes >= this._successThreshold) {
                this._successes = 0;
                this._transition(CB_STATE.CLOSED);
            }
        }
    }

    recordFailure() {
        this._lastFailureAt = Date.now();
        this._successes     = 0;
        this._failures++;
        if (this._failures >= this._failureThreshold && this._state !== CB_STATE.OPEN) {
            this._transition(CB_STATE.OPEN);
        }
    }

    _transition(newState) {
        const old = this._state;
        this._state = newState;
        if (old !== newState && this._onStateChange) this._onStateChange(newState, old);
    }

    get snapshot() {
        return { state: this._state, failures: this._failures, lastFailureAt: this._lastFailureAt };
    }

    reset() { this._failures = 0; this._successes = 0; this._transition(CB_STATE.CLOSED); }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 5 — METRICS STORE
// Tracks per-endpoint latency, success/failure counts, and last-seen errors
// with a fixed-size ring buffer for rolling-window stats.
// ─────────────────────────────────────────────────────────────────────────────

class MetricsStore {
    constructor(windowSize = 100) {
        this._windowSize = windowSize;
        this._endpoints  = new Map();    // endpoint → { latencies, successes, failures, lastError }
    }

    _ensure(endpoint) {
        if (!this._endpoints.has(endpoint)) {
            this._endpoints.set(endpoint, { latencies: [], successes: 0, failures: 0, lastError: null });
        }
        return this._endpoints.get(endpoint);
    }

    record(endpoint, latencyMs, success, error = null) {
        const m = this._ensure(endpoint);
        if (m.latencies.length >= this._windowSize) m.latencies.shift();
        m.latencies.push(latencyMs);
        if (success) { m.successes++; }
        else {
            m.failures++;
            m.lastError = { message: error?.message, code: error?.code, at: new Date().toISOString() };
        }
    }

    getSnapshot(endpoint) {
        const m = this._endpoints.get(endpoint);
        if (!m) return null;
        const lats  = m.latencies;
        const total = m.successes + m.failures;
        return {
            endpoint,
            successRate:  total > 0 ? ((m.successes / total) * 100).toFixed(1) + '%' : '—',
            p50Ms:  this._percentile(lats, 50),
            p95Ms:  this._percentile(lats, 95),
            p99Ms:  this._percentile(lats, 99),
            samples: lats.length,
            lastError: m.lastError,
        };
    }

    getAllSnapshots() {
        return Array.from(this._endpoints.keys()).map(ep => this.getSnapshot(ep)).filter(Boolean);
    }

    _percentile(arr, p) {
        if (!arr.length) return null;
        const sorted = [...arr].sort((a, b) => a - b);
        const idx    = Math.ceil((p / 100) * sorted.length) - 1;
        return sorted[Math.max(0, idx)];
    }

    clear() { this._endpoints.clear(); }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 6 — SIMPLE EVENT EMITTER
// A minimal browser-safe EventEmitter. Listeners registered via `on` can be
// removed via `off`. `once` registers a self-removing handler.
// ─────────────────────────────────────────────────────────────────────────────

class EventEmitter {
    constructor() { this._handlers = new Map(); }

    on(event, handler) {
        if (!this._handlers.has(event)) this._handlers.set(event, new Set());
        this._handlers.get(event).add(handler);
        return () => this.off(event, handler);  // returns unsubscribe fn
    }

    once(event, handler) {
        const wrapper = (...args) => { handler(...args); this.off(event, wrapper); };
        return this.on(event, wrapper);
    }

    off(event, handler) {
        this._handlers.get(event)?.delete(handler);
    }

    emit(event, payload) {
        this._handlers.get(event)?.forEach(h => {
            try { h(payload); } catch (e) { console.error(`[EventEmitter] Handler error on "${event}"`, e); }
        });
    }

    removeAllListeners(event) {
        if (event) this._handlers.delete(event);
        else       this._handlers.clear();
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 7 — OFFLINE QUEUE
// Persists mutation requests (POST/PUT/PATCH) to sessionStorage when offline.
// Flushes on `navigator.onLine` → true via the 'online' DOM event.
// ─────────────────────────────────────────────────────────────────────────────

const OFFLINE_QUEUE_KEY = 'roadAI_offlineQueue';

class OfflineQueue {
    constructor(flushFn, emitter) {
        this._flushFn  = flushFn;
        this._emitter  = emitter;
        this._draining = false;
        if (typeof window !== 'undefined') {
            window.addEventListener('online', () => this._drain());
        }
    }

    enqueue(item) {
        const queue = this._read();
        queue.push({ ...item, queuedAt: new Date().toISOString() });
        this._write(queue);
        this._emitter.emit('offline:queued', { count: queue.length, item });
    }

    async _drain() {
        if (this._draining) return;
        this._draining = true;
        const queue = this._read();
        if (!queue.length) { this._draining = false; return; }
        const remaining = [];
        for (const item of queue) {
            try {
                await this._flushFn(item);
                this._emitter.emit('offline:flushed', { item });
            } catch {
                remaining.push(item);    // keep failed items for next drain attempt
            }
        }
        this._write(remaining);
        this._draining = false;
    }

    _read() {
        try { return JSON.parse(sessionStorage.getItem(OFFLINE_QUEUE_KEY) || '[]'); }
        catch { return []; }
    }

    _write(queue) {
        try { sessionStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue)); }
        catch { /* storage full — silently drop */ }
    }

    get depth() { return this._read().length; }
    clear()     { try { sessionStorage.removeItem(OFFLINE_QUEUE_KEY); } catch {} }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 8 — CACHE TTL CONFIG
// Fine-grained TTLs per endpoint. Insights are batched ML results that rarely
// change mid-session; anomaly log changes frequently.
// ─────────────────────────────────────────────────────────────────────────────

const TTL = Object.freeze({
    networkAIInsights:     60_000,        // --- UPDATE: 1 min (the server caches 60 s too)
    pciForecast:           10 * 60_000,   // 10 min — RSF forecast per segment
    budgetRecommendations: 15 * 60_000,   // 15 min — budget model output
    rulScores:             3  * 60_000,   // 3 min  — RUL scores update after predictions
    anomalyLog:            60_000,        // 1 min  — anomalies surface quickly
    riskMap:               5  * 60_000,   // 5 min
    maintenancePriorities: 5  * 60_000,   // 5 min
    health:                30_000,        // 30 sec — health probes
});

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 9 — RETRY UTILITY
// Exponential backoff with full jitter: delay = rand(0, base * 2^attempt)
// Only retries on errors flagged `.retryable = true`.
// ─────────────────────────────────────────────────────────────────────────────

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

async function withRetry(fn, { maxAttempts = 3, baseDelayMs = 500, maxDelayMs = 15_000, logger } = {}) {
    let lastError;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            return await fn(attempt);
        } catch (err) {
            lastError = err;
            const isRetryable = err?.retryable !== false;    // default to retryable if not specified
            if (!isRetryable || attempt === maxAttempts - 1) throw err;

            // Special handling: respect Retry-After for rate-limit errors
            const delayMs = err instanceof RateLimitError
                ? err.retryAfterMs
                : Math.min(maxDelayMs, Math.random() * (baseDelayMs * Math.pow(2, attempt)));

            logger?.warn(`Retry ${attempt + 1}/${maxAttempts - 1} in ${Math.round(delayMs)}ms`, { error: err.code });
            await sleep(delayMs);
        }
    }
    throw lastError;
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 10 — HTTP RESPONSE → TYPED ERROR MAPPER
// Normalises raw fetch + HTTP errors into the typed hierarchy above.
// ─────────────────────────────────────────────────────────────────────────────

function mapHttpError(response, endpoint) {
    const ctx = { status: response.status, endpoint };
    switch (response.status) {
        case 401: case 403:
            return new AuthError(`Unauthorized — ${response.status} from ${endpoint}`, ctx);
        case 404:
            return new NotFoundError(`Endpoint not found: ${endpoint}`, ctx);
        case 422:
            return new ValidationError(`Unprocessable request to ${endpoint}`, ctx);
        case 429: {
            const retryAfter = parseInt(response.headers?.get?.('Retry-After') || '60', 10) * 1000;
            return new RateLimitError(retryAfter, ctx);
        }
        default:
            return new NetworkError(`HTTP ${response.status} from ${endpoint}`, ctx);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 11 — MAIN SERVICE CLASS
// All public methods go through the same internal request pipeline:
// Offline check → Circuit breaker → Cache check → Dedup → Retry → HTTP →
// Response normalisation → Cache write → Metrics → Event emit
// ─────────────────────────────────────────────────────────────────────────────

class RoadAIEngineService {

    constructor() {
        // Infrastructure
        this._logger  = new ServiceLogger('RoadAIEngine');
        this._cache   = new LRUCache(128);
        this._metrics = new MetricsStore(200);
        this._events  = new EventEmitter();
        this._inflight = new Set();
        this._circuit = new CircuitBreaker({
            failureThreshold: 15,
            successThreshold: 1,
            openDurationMs:   10_000,
            onStateChange:    (next, prev) => {
                this._logger[next === CB_STATE.OPEN ? 'error' : 'info'](`Circuit ${prev} → ${next}`);
                this._events.emit('circuit:' + next.toLowerCase(), { state: next, prev });
            },
        });

        // In-flight request deduplication map: cacheKey → Promise
        this._inflight = new Map();

        // Per-request AbortController registry: requestId → AbortController
        this._controllers = new Map();

        // Offline queue — flushes mutations when device reconnects
        this._offlineQueue = new OfflineQueue(
            (item) => this._rawRequest(item.endpoint, item.options, item.cacheKey, item.ttlMs),
            this._events
        );

        // Periodic cache pruning (every 2 minutes)
        if (typeof setInterval !== 'undefined') {
            this._pruneInterval = setInterval(() => this._cache.prune(), 120_000);
        }

        // Background health probe (every 30s) — lazy start on first API call
        this._healthProbeInterval = null;
        this._lastHealthReport    = null;

        this._logger.info('Service initialised');
    }

    // ── Public event bus ──────────────────────────────────────────────────────

    /**
     * Subscribe to a service event.
     * Events: request:success · request:error · circuit:open · circuit:closed ·
     *         circuit:half_open · cache:hit · cache:miss · offline:queued ·
     *         offline:flushed · health:change
     * @param {string}   event
     * @param {function} handler
     * @returns {function} unsubscribe
     */
    on(event, handler)  { return this._events.on(event, handler); }
    off(event, handler) { this._events.off(event, handler); }

    // ── Core request pipeline ─────────────────────────────────────────────────

    /**
     * Central request dispatcher. All public methods funnel through here.
     * @param {string}  endpoint     — relative API path, e.g. '/analytics/road-ai-insights'
     * @param {object}  options      — fetch options (method, body, headers, signal)
     * @param {string}  cacheKey     — unique cache key for this request
     * @param {number}  ttlMs        — cache TTL in milliseconds (0 = skip cache)
     * @param {boolean} isMutation   — true for POST/PUT/PATCH (bypasses cache, queues offline)
     * @returns {Promise<any>}
     */
    async _request(endpoint, options = {}, cacheKey = null, ttlMs = 0, isMutation = false) {
        this._startHealthProbe();

        // 1. Offline gate — mutations get queued; reads get a stale cache fallback
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            if (isMutation) {
                this._offlineQueue.enqueue({ endpoint, options, cacheKey, ttlMs });
                throw new OfflineError(endpoint);
            }
            // For reads, try stale cache before failing
            if (cacheKey) {
                const stale = this._cache.get(cacheKey);
                if (stale) {
                    this._logger.warn(`Offline — serving stale cache for ${endpoint}`);
                    return stale;
                }
            }
            throw new OfflineError(endpoint);
        }

        // 2. Circuit breaker gate
        if (!this._circuit.canRequest()) {
            this._logger.warn(`Circuit OPEN — rejecting ${endpoint}`);
            throw new CircuitOpenError(endpoint);
        }

        // 3. Cache check (reads only)
        if (!isMutation && cacheKey && ttlMs > 0) {
            const hit = this._cache.get(cacheKey);
            if (hit !== null) {
                this._logger.debug(`Cache HIT: ${cacheKey}`);
                this._events.emit('cache:hit', { key: cacheKey, endpoint });
                return hit;
            }
            this._events.emit('cache:miss', { key: cacheKey, endpoint });
        }

        // 4. Request deduplication — share the same in-flight promise for identical concurrent reads
        if (!isMutation && cacheKey && this._inflight.has(cacheKey)) {
            this._logger.debug(`Dedup: sharing in-flight request for ${cacheKey}`);
            return this._inflight.get(cacheKey);
        }

        const promise = withRetry(
            (attempt) => this._rawRequest(endpoint, options, cacheKey, ttlMs, isMutation, attempt),
            { maxAttempts: isMutation ? 1 : 3, baseDelayMs: 600, maxDelayMs: 12_000, logger: this._logger }
        );

        // Register in-flight for dedup
        if (!isMutation && cacheKey) {
            this._inflight.set(cacheKey, promise);
            promise.finally(() => this._inflight.delete(cacheKey));
        }

        return promise;
    }

    /**
     * Executes a single HTTP attempt. Called by withRetry for each attempt.
     */
    async _rawRequest(endpoint, options = {}, cacheKey = null, ttlMs = 0, isMutation = false, attempt = 0) {
        const requestId  = `${endpoint}:${Date.now()}:${Math.random().toString(36).slice(2,6)}`;
        const controller = new AbortController();
        const TIMEOUT_MS = options.timeoutMs ?? 30_000;

        this._controllers.set(requestId, controller);

        const timeoutId = setTimeout(() => {
            controller.abort();
            this._controllers.delete(requestId);
        }, TIMEOUT_MS);

        const t0 = performance.now();
        let response;

        try {
            this._logger.debug(`→ ${options.method || 'GET'} ${endpoint}`, { attempt });

            // 🚀 FIX: Use native fetch + token to guarantee a standard Response object
            const token = getAuthToken();
            const fullUrl = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;

            response = await fetch(fullUrl, {
                ...options,
                signal: controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Request-ID': requestId,
                    'X-Client':     'FlowSense-MOW-v2',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    ...(options.headers || {}),
                },
            });
        } catch (err) {
            clearTimeout(timeoutId);
            this._controllers.delete(requestId);
            const latency = performance.now() - t0;

            // AbortError fired by our own timeout → TimeoutError
            if (err.name === 'AbortError') {
                const timeoutErr = new TimeoutError(endpoint, TIMEOUT_MS);
                this._circuit.recordFailure();
                this._metrics.record(endpoint, latency, false, timeoutErr);
                this._events.emit('request:error', { endpoint, error: timeoutErr, latency });
                throw timeoutErr;
            }
            const networkErr = new NetworkError(err.message, { endpoint, cause: err });
            this._circuit.recordFailure();
            this._metrics.record(endpoint, latency, false, networkErr);
            this._events.emit('request:error', { endpoint, error: networkErr, latency });
            throw networkErr;
        }

        clearTimeout(timeoutId);
        this._controllers.delete(requestId);
        const latency = Math.round(performance.now() - t0);

        if (!response.ok) {
            const typedErr = mapHttpError(response, endpoint);
            // --- UPDATE (road RUL v2): show the server's own message ("ML engine is offline …")
            try {
                const body = await response.clone().json();
                if (body?.message) { typedErr.message = body.message; typedErr.serverMessage = body.message; }
                if (response.status === 503) typedErr.retryable = false;
            } catch { /* no JSON body */ }
            // --- END UPDATE
            // Auth errors should not trip the circuit (they're a config issue, not an outage)
            if (!(typedErr instanceof AuthError)) this._circuit.recordFailure();
            this._metrics.record(endpoint, latency, false, typedErr);
            this._events.emit('request:error', { endpoint, error: typedErr, latency });
            throw typedErr;
        }

        // Parse JSON — gracefully handle empty bodies (204 No Content)
        let data;
        try {
            data = response.status === 204 ? null : await response.json();
        } catch {
            data = null;
        }

        this._circuit.recordSuccess();
        this._metrics.record(endpoint, latency, true);
        this._logger.debug(`← ${latency}ms ${endpoint}`, { status: response.status });
        this._events.emit('request:success', { endpoint, latency, cached: false });

        // Write to cache (reads only)
        if (!isMutation && cacheKey && ttlMs > 0 && data !== null) {
            this._cache.set(cacheKey, data, ttlMs);
        }

        return data;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION 12 — PUBLIC API METHODS
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Fetch the latest ML-generated insights for the road network.
     * Includes PCI degradation forecasts, risk hotspots, and budget flags.
     * Cached for 5 minutes; safe to call on every dashboard load.
     *
     * @param {object} [opts]
     * @param {boolean} [opts.forceRefresh]  — bypass cache and fetch fresh data
     * @param {string}  [opts.constituency]  — filter insights to a specific constituency
     * @returns {Promise<RoadAIInsight[]>}
     */
    async getNetworkAIInsights({ forceRefresh = false, constituency = null } = {}) {
        const params    = constituency ? `?constituency=${encodeURIComponent(constituency)}` : '';
        const endpoint  = `/analytics/road-ai-insights${params}`;
        const cacheKey  = `networkAIInsights:${constituency || 'all'}`;

        if (forceRefresh) this._cache.invalidate(cacheKey);

        try {
            const data = await this._request(endpoint, { method: 'GET' }, cacheKey, TTL.networkAIInsights);
            // --- UPDATE (Road insights v2): the API answers { success, data: [...] } — reading only
            // data.insights threw every insight away, so the panel always showed "Standing By".
            return Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : (data?.insights ?? []));
            // --- END UPDATE
        } catch (err) {
            this._logger.error('getNetworkAIInsights failed', err.toJSON?.());
            return [];
        }
    }

    /**
     * Trigger a batch Random Survival Forest RUL prediction against all
     * active road segments. This is a long-running job — the response
     * includes a job ID for polling via pollBatchJob().
     *
     * @param {object} [opts]
     * @param {string[]} [opts.roadIds]         — limit prediction to specific road IDs
     * @param {string}   [opts.predictionDate]  — ISO date for forecast horizon (default: today)
     * @returns {Promise<BatchJobReceipt>}
     */
    async runBatchPredictiveAnalysis({ roadIds = null, predictionDate = null } = {}) {
        const body = {
            target:          'roads',
            ...(roadIds?.length && { road_ids: roadIds }),
            ...(predictionDate && { prediction_date: predictionDate }),
        };
        const data = await this._request(
            '/ml/predict-rul/batch',
            { method: 'POST', body: JSON.stringify(body) },
            null, 0, true
        );
        // --- UPDATE (road RUL v2): the backend answers 200 with the result when the run finishes
        // within ~25 s, or 202 + jobId while it is still running — wait for it either way so the
        // caller's "Analysis complete" refresh shows the new numbers.
        let result = data;
        if (data?.jobId && data?.status && data.status !== 'completed' && data.status !== 'failed') {
            const job = await this.awaitBatchJob(data.jobId, { intervalMs: 3_000, maxWaitMs: 10 * 60_000 });
            let summary = null;
            try { summary = typeof job.result_summary === 'string' ? JSON.parse(job.result_summary) : job.result_summary; } catch { summary = null; }
            result = { ...data, status: 'completed', data: summary, message: summary ? `Predicted ${summary.predicted} of ${summary.roads} road(s).` : data.message };
        }
        // Invalidate cached RUL scores since a new prediction was triggered
        ['rulScores', 'roadNetworkRUL', 'networkAIInsights', 'riskMap', 'maintenancePriorities', 'pciForecast'].forEach(k => this._cache.invalidate(k));
        return result;
        // --- END UPDATE
    }

    /**
     * Poll the status of a long-running batch ML job.
     * @param {string} jobId — returned by runBatchPredictiveAnalysis
     * @returns {Promise<BatchJobStatus>}
     */
    async pollBatchJob(jobId) {
        if (!jobId) throw new ValidationError('jobId is required for pollBatchJob', { method: 'pollBatchJob' });
        const cacheKey = `batchJob:${jobId}`;
        return this._request(`/ml/jobs/${jobId}`, { method: 'GET' }, cacheKey, 0); // --- UPDATE: never cache a status that is being polled
    }

    /**
     * Wait for a batch job to complete by polling every intervalMs.
     * Resolves when job status is 'completed' or 'failed'.
     * Rejects after maxWaitMs.
     *
     * @param {string} jobId
     * @param {object} [opts]
     * @param {number} [opts.intervalMs]   — polling interval (default 5s)
     * @param {number} [opts.maxWaitMs]    — total wait limit (default 5 min)
     * @returns {Promise<BatchJobStatus>}
     */
    awaitBatchJob(jobId, { intervalMs = 5_000, maxWaitMs = 300_000 } = {}) {
        return new Promise((resolve, reject) => {
            const start = Date.now();
            const poll = async () => {
                if (Date.now() - start > maxWaitMs) {
                    return reject(new TimeoutError(`pollBatchJob:${jobId}`, maxWaitMs));
                }
                try {
                    const status = await this.pollBatchJob(jobId);
                    if (status?.status === 'completed') return resolve(status);
                    if (status?.status === 'failed')    return reject(new RoadAIError(status.error_message ?? status.error ?? 'Job failed', 'JOB_FAILED', { jobId })); // --- UPDATE
                    setTimeout(poll, intervalMs);
                } catch (err) {
                    reject(err);
                }
            };
            poll();
        });
    }

    /**
     * Fetch PCI (Pavement Condition Index) forecasts for all road segments.
     * Returns per-segment score projections at 6-month intervals up to 36 months.
     *
     * @param {object} [opts]
     * @param {number} [opts.horizonMonths]  — forecast horizon (default 24)
     * @param {string} [opts.constituency]   — filter by constituency
     * @returns {Promise<PCIForecast[]>}
     */
    async getPCIForecast({ horizonMonths = 24, constituency = null } = {}) {
        const params   = new URLSearchParams({ horizon_months: horizonMonths });
        if (constituency) params.set('constituency', constituency);
        const endpoint = `/ml/pci-forecast?${params}`;
        const cacheKey = `pciForecast:${horizonMonths}:${constituency || 'all'}`;

        return this._request(endpoint, { method: 'GET' }, cacheKey, TTL.pciForecast);
    }

    /**
     * Fetch ML-generated budget recommendations for the coming fiscal period.
     * The model weighs PCI degradation rates, traffic counts, and historical
     * repair costs to recommend rehabilitation priorities.
     *
     * @param {object} [opts]
     * @param {string} [opts.fiscalYear]     — e.g. '2025-2026' (default: current)
     * @param {string} [opts.constituency]   — filter by constituency
     * @param {string} [opts.strategy]       — 'cost_optimal' | 'risk_minimal' | 'balanced'
     * @returns {Promise<BudgetRecommendation[]>}
     */
    async getBudgetRecommendations({ fiscalYear = null, constituency = null, strategy = 'balanced' } = {}) {
        const params = new URLSearchParams({ strategy });
        if (fiscalYear)   params.set('fiscal_year',   fiscalYear);
        if (constituency) params.set('constituency',  constituency);
        const endpoint = `/ml/budget-recommendations?${params}`;
        const cacheKey = `budgetRecommendations:${fiscalYear || 'current'}:${constituency || 'all'}:${strategy}`;

        return this._request(endpoint, { method: 'GET' }, cacheKey, TTL.budgetRecommendations);
    }

    /**
     * Fetch current Remaining Useful Life scores for all road segments.
     * Scores are derived from the most recent RSF model run.
     *
     * @param {object} [opts]
     * @param {string}   [opts.condition]    — filter: 'critical' | 'poor' | 'fair' | 'good' | 'excellent'
     * @param {string}   [opts.constituency] — filter by constituency
     * @param {boolean}  [opts.forceRefresh] — bypass cache
     * @returns {Promise<RULScore[]>}
     */
    async getRULScores({ condition = null, constituency = null, forceRefresh = false } = {}) {
        const params = new URLSearchParams();
        if (condition)    params.set('condition',     condition);
        if (constituency) params.set('constituency',  constituency);
        const endpoint = `/ml/rul-scores${params.toString() ? `?${params}` : ''}`;
        const cacheKey = `rulScores:${condition || 'all'}:${constituency || 'all'}`;

        if (forceRefresh) this._cache.invalidate(cacheKey);

        return this._request(endpoint, { method: 'GET' }, cacheKey, TTL.rulScores);
    }

    /**
     * Fetch the anomaly log — sensor-detected irregularities and Isolation
     * Forest outliers flagged against road infrastructure.
     *
     * @param {object} [opts]
     * @param {number} [opts.limit]          — max records (default 50)
     * @param {string} [opts.since]          — ISO timestamp lower bound
     * @param {string} [opts.severity]       — 'low' | 'medium' | 'high' | 'critical'
     * @returns {Promise<AnomalyRecord[]>}
     */
    async getAnomalyLog({ limit = 50, since = null, severity = null } = {}) {
        const params = new URLSearchParams({ limit });
        if (since)    params.set('since',    since);
        if (severity) params.set('severity', severity);
        const endpoint = `/ml/anomaly-log?${params}`;
        const cacheKey = `anomalyLog:${limit}:${since || 'latest'}:${severity || 'all'}`;

        return this._request(endpoint, { method: 'GET' }, cacheKey, TTL.anomalyLog);
    }

    /**
     * Fetch a risk-scored map of road segments, suitable for rendering a
     * heatmap overlay in the GIS tab.
     *
     * @param {object} [opts]
     * @param {string} [opts.riskType]  — 'structural' | 'flood' | 'traffic' | 'composite'
     * @returns {Promise<RiskMapEntry[]>}
     */
    async getRiskMap({ riskType = 'composite' } = {}) {
        const endpoint = `/ml/risk-map?risk_type=${riskType}`;
        const cacheKey = `riskMap:${riskType}`;
        return this._request(endpoint, { method: 'GET' }, cacheKey, TTL.riskMap);
    }

    /**
     * Fetch ML-ranked maintenance priorities — an ordered list of road
     * segments the model considers the highest intervention priority.
     *
     * @param {object} [opts]
     * @param {number} [opts.topN]           — return top N (default 20)
     * @param {string} [opts.constituency]   — filter by constituency
     * @returns {Promise<MaintenancePriority[]>}
     */
    async getMaintenancePriorities({ topN = 20, constituency = null } = {}) {
        const params = new URLSearchParams({ top_n: topN });
        if (constituency) params.set('constituency', constituency);
        const endpoint = `/ml/maintenance-priorities?${params}`;
        const cacheKey = `maintenancePriorities:${topN}:${constituency || 'all'}`;
        return this._request(endpoint, { method: 'GET' }, cacheKey, TTL.maintenancePriorities);
    }

    /**
     * Submit corrective feedback to the ML model (e.g. engineer overriding
     * a predicted RUL for a specific road segment). This updates the training
     * data for the next model iteration.
     *
     * @param {object} feedback
     * @param {string} feedback.roadId         — road segment ID
     * @param {string} feedback.predictionId   — prediction being corrected
     * @param {number} feedback.correctedRulYears
     * @param {string} feedback.reason         — human-readable correction reason
     * @returns {Promise<void>}
     */
    async submitPredictionFeedback({ roadId, predictionId, correctedRulYears, reason }) {
        if (!roadId || !predictionId) {
            throw new ValidationError('roadId and predictionId are required for feedback', { method: 'submitPredictionFeedback' });
        }
        await this._request(
            '/ml/prediction-feedback',
            { method: 'POST', body: JSON.stringify({ road_id: roadId, prediction_id: predictionId, corrected_rul_years: correctedRulYears, reason }) },
            null, 0, true
        );
        // Stale RUL scores after feedback submission
        this._cache.invalidate('rulScores');
        this._cache.invalidate('networkAIInsights');
    }

    /**
     * Fetch aggregate model performance metrics — accuracy, precision, recall
     * for the current production model version.
     *
     * @returns {Promise<ModelPerformanceReport>}
     */
    async getModelPerformance() {
        return this._request(
            '/ml/model-performance',
            { method: 'GET' },
            'modelPerformance',
            TTL.pciForecast       // same cadence — rarely changes mid-session
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // NEW: EDGE TELEMETRY & CLUSTERING
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Triggers the Node.js clustering service to group raw pothole detections
     * into actionable Work Orders.
     * Handled as a mutation (bypasses cache, queues offline if disconnected).
     *
     * @returns {Promise<{ success: boolean, clusters: number }>}
     */
    async triggerAnomalyClustering() {
        return this._request(
            '/roads/telemetry/cluster',
            { method: 'POST' },
            null,
            0,
            true // isMutation: queues in sessionStorage if offline, skips cache
        );
    }

    /**
     * Fetches the Predictive RUL (Remaining Useful Life) for all road segments
     * to render the Heatmap layer.
     * Cached with RUL scores TTL (3 min) and shares in-flight deduplication.
     *
     * @param {object} [opts]
     * @param {boolean} [opts.forceRefresh] - Bypass cache to fetch fresh scores
     * @returns {Promise<Array<{ id: string, name: string, predicted_rul_years: number, confidence_score: number }>>}
     */
    async getRoadNetworkRUL({ forceRefresh = false } = {}) {
        const endpoint = '/roads/analytics/rul-network';
        const cacheKey = 'roadNetworkRUL';

        if (forceRefresh) {
            this._cache.invalidate(cacheKey);
        }

        const data = await this._request(
            endpoint,
            { method: 'GET' },
            cacheKey,
            TTL.rulScores
        );

        return Array.isArray(data) ? data : (data?.data ?? []);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION 13 — CACHE MANAGEMENT API
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Invalidate cache entries by prefix. Use when you know data has changed.
     * @param {string} prefix  — e.g. 'rulScores', 'budgetRecommendations'
     * @returns {number} number of cache entries evicted
     */
    invalidateCache(prefix) {
        const evicted = prefix ? this._cache.invalidate(prefix) : (this._cache.clear(), Infinity);
        this._logger.info(`Cache invalidated: ${prefix ?? 'ALL'} (${evicted} entries)`);
        return evicted;
    }

    /** Wipe the entire in-memory cache. Useful after a bulk data import. */
    clearCache() {
        this._cache.clear();
        this._logger.info('Cache fully cleared');
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION 14 — ABORT / CANCELLATION API
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Cancel all in-flight requests. Call this on component unmount or
     * before navigating away to prevent state-on-unmounted-component warnings.
     */
    cancelAll() {
        let cancelled = 0;
        for (const [id, controller] of this._controllers) {
            controller.abort();
            this._controllers.delete(id);
            cancelled++;
        }
        this._logger.info(`Cancelled ${cancelled} in-flight request(s)`);
        return cancelled;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION 15 — HEALTH MONITOR
    // ─────────────────────────────────────────────────────────────────────────

    _startHealthProbe() {
        if (this._healthProbeInterval) return;
        this._probeHealth();
        // --- UPDATE (performance): every 2 min and only while the tab is visible (was every 30 s
        // in every open tab, three backend queries each time)
        this._healthProbeInterval = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            this._probeHealth();
        }, 120_000);
        // --- END UPDATE
    }

    async _probeHealth() {
        const endpoints = [
            '/analytics/road-ai-insights',
            '/ml/rul-scores',
            '/ml/anomaly-log',
        ];
        const results = await Promise.allSettled(
            endpoints.map(async (ep) => {
                const t0     = performance.now();
                const signal = AbortSignal.timeout ? AbortSignal.timeout(8_000) : undefined;
                await this._rawRequest(ep + '?limit=1', { method: 'GET', signal }, null, 0).catch(() => null);
                return { ep, latencyMs: Math.round(performance.now() - t0) };
            })
        );

        const report = {
            timestamp:     new Date().toISOString(),
            circuit:       this._circuit.snapshot,
            cache:         this._cache.stats,
            offlineQueue:  this._offlineQueue.depth,
            endpoints:     results.map((r, i) => ({
                path:   endpoints[i],
                status: r.status === 'fulfilled' ? 'ok' : 'error',
                ...(r.status === 'fulfilled' && { latencyMs: r.value.latencyMs }),
            })),
        };

        const prev = this._lastHealthReport;
        this._lastHealthReport = report;

        // Emit only if health status changed materially
        const prevOkCount = prev?.endpoints.filter(e => e.status === 'ok').length ?? -1;
        const currOkCount = report.endpoints.filter(e => e.status === 'ok').length;
        if (prevOkCount !== currOkCount) {
            this._events.emit('health:change', report);
            this._logger.info(`Health changed: ${currOkCount}/${endpoints.length} endpoints reachable`);
        }

        return report;
    }

    /**
     * Returns the most recent health probe result synchronously.
     * Triggers a fresh probe in the background if no report exists yet.
     * @returns {object|null}
     */
    getHealthReport() {
        if (!this._lastHealthReport) this._probeHealth();
        return this._lastHealthReport;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION 16 — OBSERVABILITY & DIAGNOSTICS
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Returns a full diagnostic snapshot suitable for the debug drawer.
     * Includes circuit state, cache stats, per-endpoint metrics, and
     * offline queue depth.
     *
     * @returns {ServiceDiagnostics}
     */
    getDiagnostics() {
        return {
            timestamp:     new Date().toISOString(),
            circuit:       this._circuit.snapshot,
            cache:         this._cache.stats,
            offlineQueue:  this._offlineQueue.depth,
            inflight:      this._inflight.size,
            metrics:       this._metrics.getAllSnapshots(),
            health:        this._lastHealthReport,
        };
    }

    /**
     * Force the circuit breaker back to CLOSED. Use only for manual recovery
     * after a confirmed service restoration.
     */
    resetCircuit() {
        this._circuit.reset();
        this._logger.warn('Circuit breaker manually reset to CLOSED');
        this._events.emit('circuit:closed', { manual: true });
    }

    /**
     * Enterprise Circuit Breaker Wrapper
     * Wraps API calls to handle failures, timeouts, and service outages gracefully.
     */
    async executeWithCircuitBreaker(name, operation) {
        if (this._circuit.state === 'OPEN') {
            this._logger.warn(`Circuit OPEN: Blocking request to ${name}`);
            throw new Error('Circuit Breaker is OPEN. Service temporarily unavailable.');
        }

        try {
            // 🚀 FIX: Map uses .set(), not .add()
            // We store the operation promise itself to ensure we don't fire duplicates
            const opPromise = operation();
            this._inflight.set(name, opPromise);
            
            const result = await opPromise;
            
            // Success: Reset failures
            this._circuit.failures = 0;
            return result;
        } catch (error) {
            this._circuit.failures++;
            this._logger.error(`Circuit failure on ${name}: ${this._circuit.failures}`);
            
            if (this._circuit.failures >= 3) {
                this._circuit.state = 'OPEN';
                this._events.emit('circuit:open');
                setTimeout(() => {
                    this._circuit.state = 'CLOSED';
                    this._circuit.failures = 0;
                    this._events.emit('circuit:closed');
                }, 30000); // 30 second cooldown
            }
            throw error;
        } finally {
            // 🚀 FIX: Correct deletion from Map
            this._inflight.delete(name);
        }
    }

    /**
     * Helper to map HTTP responses to our Typed Error hierarchy.
     * Proxies to the global mapHttpError function.
     */
    _handleError(response, endpoint = 'AI_ENGINE') {
        return mapHttpError(response, endpoint);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION 17 — LIFECYCLE / TEARDOWN
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Gracefully shut down the service. Cancels all in-flight requests,
     * stops background intervals, and removes event listeners. Call this
     * if the service is being unmounted at the application level.
     */
    destroy() {
        this.cancelAll();
        if (this._pruneInterval)       clearInterval(this._pruneInterval);
        if (this._healthProbeInterval) clearInterval(this._healthProbeInterval);
        this._events.removeAllListeners();
        this._cache.clear();
        this._inflight.clear();
        this._logger.info('Service destroyed — all resources released');
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTS
// The service is exported as a singleton (matches existing consumer usage).
// The classes are also named-exported for unit testing and DI scenarios.
// ─────────────────────────────────────────────────────────────────────────────

export {
    RoadAIEngineService,
    CircuitBreaker,
    LRUCache,
    MetricsStore,
    EventEmitter,
    OfflineQueue,
    // Error types — consumers can instanceof-check these in catch blocks
    RoadAIError,
    NetworkError,
    AuthError,
    NotFoundError,
    ValidationError,
    RateLimitError,
    CircuitOpenError,
    TimeoutError,
    OfflineError,
};

export default new RoadAIEngineService();